import { useCallback, useEffect, useRef, useState } from 'react';
import {
  layout, cursorAt, isAhead, resumePoint, backTarget, forwardTarget
} from './narrationTimeline';

// One AudioContext for the whole app. Browsers only let audio start after a
// user gesture, and Safari only counts a resume() made inside the gesture
// itself, so the context is primed on the first tap or key press anywhere.
let sharedContext = null;
// sources → bus (one per schedule) → analyser → master volume → speakers.
// The analyser sits before the volume so the wave still follows the voice
// when it is muted.
let sharedGraph = null;

const getAudioContext = () => {
  if (!sharedContext) {
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!Context) return null;
    sharedContext = new Context();

    const analyser = sharedContext.createAnalyser();
    // ~45 Hz bins and ~20 ms of waveform per frame, lightly smoothed so the
    // wave follows syllables; the dB window suits Kokoro's speech level.
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.55;
    analyser.minDecibels = -85;
    analyser.maxDecibels = -20;
    const master = sharedContext.createGain();
    analyser.connect(master);
    master.connect(sharedContext.destination);
    sharedGraph = { analyser, master };
  }
  return sharedContext;
};

const primeAudio = () => {
  const context = getAudioContext();
  if (context?.state === 'suspended') context.resume().catch(() => {});
};

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', primeAudio, { capture: true });
  document.addEventListener('keydown', primeAudio, { capture: true });
}

const base64ToArrayBuffer = (base64) => {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return bytes.buffer;
};

// Small lead so the first sentence is not clipped while the graph spins up.
const START_LEAD = 0.08;
// Fade on stop, so cutting a sentence mid-word does not click.
const FADE = 0.03;

/**
 * A bus is everything scheduled by one call to `schedule`. Seeking or
 * pausing fades the whole bus out and starts a fresh one, which is simpler
 * than tracking which source plays when.
 */
const openBus = (context) => {
  const gain = context.createGain();
  gain.connect(sharedGraph.analyser);
  return { context, gain, sources: [] };
};

const closeBus = (bus) => {
  if (!bus) return;
  const { context, gain, sources } = bus;
  const now = context.currentTime;
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(gain.gain.value, now);
  gain.gain.linearRampToValueAtTime(0, now + FADE);
  sources.forEach(source => {
    try { source.stop(now + FADE); } catch (err) { /* already stopped */ }
  });
  setTimeout(() => gain.disconnect(), FADE * 1000 + 250);
};

/** Plays an entry on its scheduled time, joining part-way if that is before `at`. */
const play = (bus, entry, at) => {
  const source = bus.context.createBufferSource();
  source.buffer = entry.buffer;
  source.connect(bus.gain);
  const when = Math.max(entry.start, at);
  source.start(when, when - entry.start);
  bus.sources.push(source);
};

const VOLUME_KEY = 'arcanos.voice.volume';
const MUTED_KEY = 'arcanos.voice.muted';

const readVolume = () => {
  try {
    const stored = parseFloat(localStorage.getItem(VOLUME_KEY));
    return Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : 1;
  } catch (err) {
    return 1;
  }
};

const readMuted = () => {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch (err) {
    return false;
  }
};

const INITIAL_VIEW = {
  spoken: 0, words: 0, reachSpoken: 0, reachWords: 0,
  count: 0, speaking: false, ended: false, blocked: false
};

const sameView = (a, b) => Object.keys(a).every(key => a[key] === b[key]);

/**
 * Plays narrated sentences back to back and reports how far the voice has got.
 *
 * `enqueue` takes the sentences from the reading stream, in order. Each is
 * scheduled on the audio clock directly after the previous one, so playback
 * is gapless. A sentence without audio takes no time: it is revealed the
 * moment the voice reaches it.
 *
 * `cursor` is `{ spoken, words }`: `spoken` sentences have been reached
 * (the last of them is the one being said) and `words` is the fraction of
 * that last sentence said so far, 0..1. `reach` is the furthest the cursor
 * has ever been, so going back does not hide text already revealed.
 *
 * Seeking re-lays the timeline from the target sentence and reschedules its
 * audio; pausing stops the audio and holds the clock where it was, and
 * resuming reschedules from there.
 */
const useNarrator = () => {
  const timeline = useRef([]);       // { buffer, duration, words, start, end } per sentence
  const bus = useRef(null);
  const nextStart = useRef(0);       // where the next sentence received will go
  const chain = useRef(Promise.resolve());
  const generation = useRef(0);      // bumped by reset(), so stale decodes are dropped
  const pending = useRef(0);         // sentences still decoding
  const held = useRef(null);         // clock time playback is held at while paused
  const reach = useRef({ spoken: 0, words: 0 });
  const frame = useRef(0);

  const [view, setView] = useState(INITIAL_VIEW);
  const [paused, setPaused] = useState(false);
  const [volume, setVolume] = useState(readVolume);
  const [muted, setMuted] = useState(readMuted);

  /** The playhead: the audio clock, or where it was held when paused. */
  const clock = useCallback(() => held.current ?? getAudioContext().currentTime, []);

  const tick = useCallback(() => {
    const context = getAudioContext();
    const now = clock();
    const entries = timeline.current;
    const live = held.current === null;

    const cursor = cursorAt(entries, now);
    if (isAhead(cursor, reach.current)) reach.current = cursor;
    const current = entries[cursor.spoken - 1];

    const next = {
      spoken: cursor.spoken,
      words: cursor.words,
      reachSpoken: reach.current.spoken,
      reachWords: reach.current.words,
      count: entries.length,
      speaking: live && context.state === 'running' && Boolean(current?.buffer) && now < current.end,
      ended: live && entries.length > 0 && pending.current === 0 && now >= nextStart.current,
      blocked: live && context.state === 'suspended' && entries.length > 0
    };
    setView(prev => (sameView(prev, next) ? prev : next));

    frame.current = requestAnimationFrame(tick);
  }, [clock]);

  useEffect(() => {
    if (!getAudioContext()) return undefined;
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [tick]);

  useEffect(() => {
    const context = getAudioContext();
    if (context) {
      sharedGraph.master.gain.setTargetAtTime(muted ? 0 : volume, context.currentTime, 0.015);
    }
    try {
      localStorage.setItem(VOLUME_KEY, String(volume));
      localStorage.setItem(MUTED_KEY, muted ? '1' : '0');
    } catch (err) {
      // Storage blocked (private mode): the level just isn't remembered.
    }
  }, [volume, muted]);

  /**
   * Lays the timeline out so `offset` seconds into sentence `index` falls on
   * `at`, and (unless paused) schedules the audio from there on.
   */
  const schedule = useCallback((index, offset, at, audible) => {
    const context = getAudioContext();
    const entries = timeline.current;

    closeBus(bus.current);
    bus.current = null;

    const { times, end } = layout(entries, index, offset, at);
    entries.forEach((entry, i) => Object.assign(entry, times[i]));
    nextStart.current = end;

    if (!audible) return;
    bus.current = openBus(context);
    for (let i = index; i < entries.length; i++) {
      if (entries[i].buffer && entries[i].end > at) play(bus.current, entries[i], at);
    }
  }, []);

  const enqueue = useCallback((sentence) => {
    const context = getAudioContext();
    if (!context) return;
    const run = generation.current;
    pending.current++;

    chain.current = chain.current.then(async () => {
      let buffer = null;
      if (sentence.audio) {
        try {
          buffer = await context.decodeAudioData(base64ToArrayBuffer(sentence.audio));
        } catch (err) {
          console.error('Could not decode narration audio:', err);
        }
      }
      if (run !== generation.current) return;
      pending.current--;

      // While paused the sentence is only placed on the held timeline;
      // resume() schedules its audio along with the rest.
      const live = held.current === null;
      const start = Math.max(nextStart.current, clock() + (live ? START_LEAD : 0));
      const duration = buffer ? buffer.duration : 0;
      const entry = { buffer, duration, words: sentence.words || [], start, end: start + duration };

      timeline.current.push(entry);
      nextStart.current = entry.end;

      if (buffer && live) {
        if (!bus.current) bus.current = openBus(context);
        play(bus.current, entry, start);
      }
    });
  }, [clock]);

  /** Silences everything queued and forgets the reading. */
  const reset = useCallback(() => {
    generation.current++;
    chain.current = Promise.resolve();
    pending.current = 0;
    closeBus(bus.current);
    bus.current = null;
    timeline.current = [];
    nextStart.current = 0;
    held.current = null;
    reach.current = { spoken: 0, words: 0 };
    setView(INITIAL_VIEW);
    setPaused(false);
  }, []);

  useEffect(() => reset, [reset]);

  const pause = useCallback(() => {
    const context = getAudioContext();
    if (!context || held.current !== null) return;
    held.current = context.currentTime;
    closeBus(bus.current);
    bus.current = null;
    setPaused(true);
  }, []);

  /** Also the way out of `blocked`: call it from a click handler. */
  const resume = useCallback(() => {
    const context = getAudioContext();
    if (!context) return;
    if (context.state === 'suspended') context.resume().catch(() => {});
    if (held.current === null) return;

    const { index, offset } = resumePoint(timeline.current, held.current);
    held.current = null;
    schedule(index, offset, context.currentTime + START_LEAD, true);
    setPaused(false);
  }, [schedule]);

  /** Jumps to the start of a sentence already received. Keeps a pause held. */
  const seekToSentence = useCallback((index) => {
    const context = getAudioContext();
    if (!context || index < 0 || index >= timeline.current.length) return;

    if (held.current !== null) schedule(index, 0, held.current, false);
    else schedule(index, 0, context.currentTime + START_LEAD, true);
  }, [schedule]);

  const back = useCallback(() => {
    const entries = timeline.current;
    if (!entries.length) return;
    const now = clock();
    const current = cursorAt(entries, now).spoken - 1;
    const elapsed = current >= 0 ? now - entries[current].start : 0;
    seekToSentence(backTarget(entries, current, elapsed));
  }, [clock, seekToSentence]);

  const forward = useCallback(() => {
    const entries = timeline.current;
    const target = forwardTarget(entries, cursorAt(entries, clock()).spoken - 1);
    if (target >= 0) seekToSentence(target);
  }, [clock, seekToSentence]);

  const toggleMute = useCallback(() => setMuted(on => !on), []);

  const supported = Boolean(getAudioContext());
  const currentIndex = view.spoken - 1;

  return {
    supported,
    analyser: supported ? sharedGraph.analyser : null,
    cursor: { spoken: view.spoken, words: view.words },
    reach: { spoken: view.reachSpoken, words: view.reachWords },
    count: view.count,
    currentIndex,
    canBack: view.count > 0,
    canForward: currentIndex >= 0 && currentIndex + 1 < view.count,
    speaking: view.speaking,
    ended: view.ended,
    paused,
    blocked: view.blocked,
    volume,
    muted,
    enqueue,
    reset,
    pause,
    resume,
    seekToSentence,
    back,
    forward,
    setVolume,
    toggleMute
  };
};

export default useNarrator;
